import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const Kanban = () => {
  const [board, setBoard] = useState({ todo: [], 'in-progress': [], review: [], done: [] });
  const [loading, setLoading] = useState(true);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium' });

  useEffect(() => {
    fetchBoard();
  }, []);

  const fetchBoard = async () => {
    try {
      const res = await axios.get('/api/tasks/board/kanban');
      setBoard(res.data.board);
    } catch (error) {
      toast.error('Failed to load board');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/tasks', newTask);
      toast.success('Task created');
      setShowNewTask(false);
      setNewTask({ title: '', description: '', priority: 'medium' });
      fetchBoard();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const moveTask = async (taskId, newStatus) => {
    try {
      await axios.put(`/api/tasks/${taskId}`, { status: newStatus });
      fetchBoard();
    } catch (error) {
      toast.error('Failed to move task');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Kanban Board</h1>
        <button
          onClick={() => setShowNewTask(true)}
          className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-colors"
        >
          + New Task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Column
          title="To Do"
          status="todo"
          tasks={board.todo}
          onMove={moveTask}
          color="border-gray-500"
        />
        <Column
          title="In Progress"
          status="in-progress"
          tasks={board['in-progress']}
          onMove={moveTask}
          color="border-blue-500"
        />
        <Column
          title="Review"
          status="review"
          tasks={board.review}
          onMove={moveTask}
          color="border-yellow-500"
        />
        <Column
          title="Done"
          status="done"
          tasks={board.done}
          onMove={moveTask}
          color="border-green-500"
        />
      </div>

      {/* New Task Modal */}
      {showNewTask && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Create New Task</h3>
            <form onSubmit={createTask} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                  className="w-full px-4 py-2 bg-[#0a0a0f] border border-[#2d2d3d] rounded-lg text-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewTask(false)}
                  className="flex-1 px-4 py-2 bg-[#2d2d3d] hover:bg-[#3d3d4d] rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Column = ({ title, status, tasks, onMove, color }) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId) {
      onMove(taskId, status);
    }
  };

  return (
    <div
      className={`bg-[#12121a] border border-[#2d2d3d] rounded-2xl p-4 min-h-[500px] ${
        isDragOver ? 'border-indigo-500 bg-indigo-500/5' : ''
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className={`flex items-center justify-between mb-4 pb-3 border-b-2 ${color}`}>
        <h3 className="font-semibold">{title}</h3>
        <span className="px-2 py-1 bg-[#0a0a0f] rounded-lg text-sm text-gray-400">
          {tasks.length}
        </span>
      </div>

      <div className="space-y-3">
        {tasks.map((task) => (
          <TaskCard key={task._id} task={task} />
        ))}
      </div>
    </div>
  );
};

const TaskCard = ({ task }) => {
  const handleDragStart = (e) => {
    e.dataTransfer.setData('taskId', task._id);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="bg-[#0a0a0f] border border-[#2d2d3d] rounded-xl p-4 cursor-move hover:border-indigo-500/50 transition-all group"
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-sm">{task.title}</h4>
        <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
      </div>
      
      {task.description && (
        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.description}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {task.assignedTo ? (
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center text-xs">
              {task.assignedTo.avatar || '🤖'}
            </div>
          ) : (
            <span className="text-xs text-gray-500">Unassigned</span>
          )}
          {task.metadata?.inferenceBackend && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${getBackendBadge(task.metadata.inferenceBackend)}`}>
              {task.metadata.inferenceBackend}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(task.status)}`}>
            {task.progress}%
          </span>
        </div>
      </div>

      {task.progress > 0 && (
        <div className="mt-3 h-1 bg-[#2d2d3d] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
            style={{ width: `${task.progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

const getPriorityColor = (priority) => {
  const colors = {
    low: 'bg-blue-500',
    medium: 'bg-yellow-500',
    high: 'bg-orange-500',
    urgent: 'bg-red-500'
  };
  return colors[priority] || 'bg-gray-500';
};

const getStatusBadge = (status) => {
  const badges = {
    'todo': 'bg-gray-500/20 text-gray-400',
    'in-progress': 'bg-blue-500/20 text-blue-400',
    'review': 'bg-yellow-500/20 text-yellow-400',
    'done': 'bg-green-500/20 text-green-400'
  };
  return badges[status] || 'bg-gray-500/20 text-gray-400';
};

const getBackendBadge = (backend) => {
  const badges = {
    local: 'bg-emerald-500/20 text-emerald-400',
    runpod: 'bg-indigo-500/20 text-indigo-400',
    openai: 'bg-amber-500/20 text-amber-400',
    anthropic: 'bg-pink-500/20 text-pink-400',
    cohere: 'bg-purple-500/20 text-purple-400'
  };
  return badges[backend] || 'bg-gray-500/20 text-gray-400';
};

export default Kanban;
